import { CSSProperties, useRef, useState } from 'react';
import cl from 'clsx';
import { useOnClickOutside } from 'usehooks-ts';

import { PartialNullable, isNonNullish } from '@synapex/tools/typescript';
import { FORMAT } from '@synapex/tools/utils';
import TrashIcon from '@synapex/frontend-shared/icons/trash-14x16.svg?react';
import CheckIcon from '@synapex/frontend-shared/icons/check-15x12.svg?react';
import CloseIcon from '@synapex/frontend-shared/icons/close-12.svg?react';

import PlaceholderImageUrl from '@/shared/assets/icons/placeholder.svg';

import { PermissionKey } from '@/true-fsd/data/advertiser-account';
import { Checkbox2 } from '@/true-fsd/shared/ui/Checkbox2';
import { IconButton } from '@/true-fsd/shared/ui/ButtonLink';
import {
  useSeparateInput,
  useTimeInput,
  type UseSeparateInputProps,
} from '@/true-fsd/shared/ui/Input';
import { GmtSelect, GmtSelectDisabled, coerceGmtValue } from './GmtSelect';
import classes from './PermissionsTable.module.scss';
import {
  FILLER_STYLE,
  TABLE_COLUMNS_LAYOUT,
  type TableReactNodeRecord,
} from './tableColumnsLayout';

type PulseDataProps = {
  send_pulse: boolean;
  time: string;
  gmt: string;
};

type PulseCbProps = {
  onPulseCheckedChange: (v: boolean) => void;
  onTimeChange: (v: string) => void;
  onGmtChange: (v: string) => void;
};

type PulseProps = PulseDataProps & PulseCbProps;

export type TableRowDataProps = {
  fullName: string;
  photoUrl?: string | null;

  admin: boolean;
  audience: boolean;

  invite_link: boolean;
  comment?: string | null;
  grantedAt?: Date | null;
} & PartialNullable<PulseDataProps>;

export type TableRowProps = TableRowDataProps & {
  ordinal: number;

  onCommentChange?: (v: string) => void;
  onPulseCheckedChange?: (checked: boolean) => void;
  onCheckedChange?: (
    key: Exclude<PermissionKey, 'send_pulse' | 'retention'>,
    checked: boolean,
  ) => void;
} & PartialNullable<PulseCbProps> &
  Partial<DeleteButtonProps>;

const separatePulseProps = ({
  send_pulse,
  time,
  gmt,
  onTimeChange,
  onGmtChange,
  onPulseCheckedChange,
  ...props
}: TableRowProps): [
  PulseProps | null,
  Omit<TableRowProps, keyof PulseProps>,
] => {
  return [
    isNonNullish(send_pulse) &&
    isNonNullish(time) &&
    isNonNullish(gmt) &&
    isNonNullish(onTimeChange) &&
    isNonNullish(onGmtChange) &&
    isNonNullish(onPulseCheckedChange)
      ? {
          send_pulse,
          time,
          gmt,
          onTimeChange,
          onGmtChange,
          onPulseCheckedChange,
        }
      : null,
    props,
  ];
};

export const TableRow = (allProps: TableRowProps) => {
  const [
    pulseProps,
    {
      onCommentChange,
      onCheckedChange: props_onCheckedChange,
      onDelete,
      ...props
    },
  ] = separatePulseProps(allProps);

  const checkboxProps = (
    key: Exclude<PermissionKey, 'retention' | 'send_pulse'>,
  ) =>
    props_onCheckedChange
      ? {
          checked: props[key],
          onCheckedChange: (checked: boolean) =>
            props_onCheckedChange(key, checked),
        }
      : {
          checked: props[key],
          disabled: true,
        };

  const tableRow: TableReactNodeRecord = {
    ordinal: <div className={classes.ordinal}>{props.ordinal}</div>,
    photo_url: (
      <img
        src={props.photoUrl ?? PlaceholderImageUrl}
        className={classes.photo}
      />
    ),
    full_name: props.fullName,
    comment: onCommentChange ? (
      <CommentInput
        onApply={onCommentChange}
        defaultValue={props.comment ?? ''}
      />
    ) : (
      <CommentStatic>{props.comment ?? ''}</CommentStatic>
    ),
    grantedAt: props.grantedAt
      ? FORMAT.date2Digit.format(props.grantedAt)
      : null,
    admin: (
      <div className={classes.checkboxHolder}>
        <Checkbox2 {...checkboxProps('admin')} />
      </div>
    ),
    audience: (
      <div className={classes.checkboxHolder}>
        <Checkbox2 {...checkboxProps('audience')} />
      </div>
    ),
    invite_link: (
      <div className={classes.checkboxHolder}>
        <Checkbox2 {...checkboxProps('invite_link')} />
      </div>
    ),
    pulse: pulseProps ? <PulseInput {...pulseProps} /> : <PulseDisabled />,
  };

  return (
    <tr>
      {TABLE_COLUMNS_LAYOUT.map(({ key, style, className }) => (
        <td key={key} style={style} className={className}>
          {tableRow[key]}
        </td>
      ))}
      <td style={FILLER_STYLE}>
        {onDelete && <DeleteButton onDelete={onDelete} />}
      </td>
    </tr>
  );
};

const CommentInput = (props: UseSeparateInputProps) => {
  return (
    <div className={classes.commentCell}>
      <input
        {...useSeparateInput(props)}
        className={cl(classes.inputBorder, classes.inputText)}
      />
    </div>
  );
};

const CommentStatic = ({
  children,
}: {
  children: string | undefined | null;
}) => {
  return (
    <div className={cl(classes.commentCell, classes.static)}>{children}</div>
  );
};

const PulseInput = ({
  gmt,
  time,
  onGmtChange,
  onTimeChange,
  send_pulse,
  onPulseCheckedChange,
}: PulseProps) => {
  return (
    <div className={classes.sendPulse}>
      <div className={classes.checkboxHolder}>
        <Checkbox2
          checked={send_pulse}
          onCheckedChange={onPulseCheckedChange}
        />
      </div>
      <input
        {...useTimeInput({ defaultValue: time, onApply: onTimeChange })}
        className={cl(
          classes.inputBorder,
          classes.inputText,
          classes.timeInput,
        )}
      />
      <GmtSelect
        value={coerceGmtValue(gmt)}
        onChange={onGmtChange}
        className={cl(classes.inputBorder, classes.inputText, classes.gmtInput)}
      />
    </div>
  );
};

const PulseDisabled = () => {
  return (
    <div className={classes.sendPulse}>
      <div className={classes.checkboxHolder}>
        <Checkbox2 checked={false} disabled />
      </div>
      <input
        disabled
        className={cl(
          classes.inputBorder,
          classes.inputText,
          classes.timeInput,
        )}
      />
      <GmtSelectDisabled
        className={cl(classes.inputBorder, classes.inputText, classes.gmtInput)}
      />
    </div>
  );
};

const STYLE = {
  check: {
    color: '#056FFF',
    width: 13,
    height: 11,
  },
  close: {
    color: '#000000',
    width: 11,
    height: 11,
  },
} satisfies Record<string, CSSProperties>;

type DeleteButtonProps = {
  onDelete: () => void;
};
const DeleteButton = ({ onDelete }: DeleteButtonProps) => {
  const [confirm, setConfirm] = useState(false);

  const onSetConfirm = () => setConfirm(true);

  const onUnsetConfirm = () => setConfirm(false);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, onUnsetConfirm, 'mouseup');

  if (confirm) {
    return (
      <div ref={ref} className={classes.deleteButton}>
        <div className={classes.confirmText}>Удалить?</div>
        <IconButton
          onClick={onDelete}
          Icon={CheckIcon}
          iconStyle={STYLE.check}
        />
        <IconButton
          onClick={onUnsetConfirm}
          Icon={CloseIcon}
          iconStyle={STYLE.close}
        />
      </div>
    );
  } else {
    return (
      <div className={classes.deleteButton}>
        <IconButton onClick={onSetConfirm} Icon={TrashIcon} />
      </div>
    );
  }
};
